import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {FileText, Loader2, Lock, User} from 'lucide-react'
import toast from 'react-hot-toast'
import { LoadingScreen } from '@/components/layout/LoadingScreen'
import { useAppAuth } from '@/contexts/AuthContext'
import { maskCPF, unmaskCPF } from '@/utils/masks'

function Login() {
  const { login, user } = useAppAuth()
  const navigate = useNavigate()
  const [cpf, setCpf] = useState('')
  const [senha, setSenha] = useState('')
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState('')
  const [entrando, setEntrando] = useState(false)

  const concluirEntrada = useCallback(() => {
    navigate('/dashboard')
  }, [navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErro('')

    if (unmaskCPF(cpf).length !== 11) {
      setErro('Informe um CPF válido.')
      return
    }
    if (!senha) {
      setErro('Informe a senha.')
      return
    }

    setLoading(true)
    const result = await login(cpf, senha)
    setLoading(false)

    if (result.success) {
      toast.success('Sessão iniciada com sucesso.')
      setEntrando(true)
    }
    else {
      setErro(result.message || 'CPF ou senha inválidos.')
    }
  }

  if (entrando) {
    return (
      <LoadingScreen
        titulo="A entrar"
        onConcluido={concluirEntrada}
        usuario={user ? { nome: user.nomeCompleto, fotoUrl: user.fotoUrl } : undefined}
      />
    )
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center px-4">
      <div className="w-full max-w-[400px]">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#1E4FBF] flex items-center justify-center shadow-sm mb-4">
            <FileText className="w-7 h-7 text-white" strokeWidth={1.75} />
          </div>
          <h1 className="text-[22px] font-semibold text-[#12141A] tracking-tight">PROTOCOLEAI</h1>
          <p className="text-[13px] text-[#6B7280] mt-1">Gestão de Protocolos Administrativos</p>
        </div>

        <div className="bg-white rounded-2xl border border-[#E7E9EC] shadow-[0_1px_2px_rgba(16,24,40,0.04)] p-8">
          <form onSubmit={handleSubmit} autoComplete="off" className="flex flex-col gap-4">
            <div>
              <label htmlFor="cpf" className="block text-[13px] font-medium text-[#374151] mb-1.5">CPF</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" strokeWidth={1.75} />
                <input
                  id="cpf"
                  type="text"
                  inputMode="numeric"
                  value={cpf}
                  onChange={e => setCpf(maskCPF(e.target.value))}
                  placeholder="000.000.000-00"
                  maxLength={14}
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] text-[#12141A] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF] transition-colors"
                  autoComplete="off"
                  name="identificacao"
                  autoCapitalize="off"
                  spellCheck={false}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="senha" className="block text-[13px] font-medium text-[#374151]">Senha</label>
                <button
                  type="button"
                  onClick={() => toast('Contacte o administrador do sistema para redefinir a senha.')}
                  className="text-[12px] text-[#1E4FBF] hover:underline font-medium"
                >
                  Esqueci minha senha
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" strokeWidth={1.75} />
                <input
                  id="senha"
                  type="password"
                  value={senha}
                  onChange={e => setSenha(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] text-[#12141A] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF] transition-colors"
                  autoComplete="new-password"
                  name="credencial"
                />
              </div>
            </div>

            {erro && (
              <div className="text-[13px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">
                {erro}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="mt-2 w-full flex items-center justify-center gap-2 bg-[#1E4FBF] hover:bg-[#1A44A6] disabled:opacity-60 text-white text-[14px] font-medium py-2.5 rounded-lg transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Entrar
            </button>
          </form>
        </div>

        <p className="text-center text-[12px] text-[#9CA3AF] mt-6">
          © {new Date().getFullYear()} PROTOCOLEAI. Todos os direitos reservados.
        </p>
      </div>
    </div>
  )
}

export default Login
